import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    doc,
    getDoc,
    deleteDoc,
    collection,
    addDoc,
    getDocs,
    query,
    orderBy,
    updateDoc,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import TemplateBank from './TemplateBank';

function calculateAge(birthDateStr) {
    const birthDate = new Date(birthDateStr);
    const now = new Date();
    let age = now.getFullYear() - birthDate.getFullYear();
    const m = now.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}

function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [newSession, setNewSession] = useState('');
    const [therapistName, setTherapistName] = useState('');
    const [therapistUID, setTherapistUID] = useState('');
    const [customTherapist, setCustomTherapist] = useState('');
    const [customDate, setCustomDate] = useState('');
    const [showMore, setShowMore] = useState(false);
    const [expandedSession, setExpandedSession] = useState(null);
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editedNote, setEditedNote] = useState('');
    const [therapists, setTherapists] = useState([]);
    const [showTemplateBank, setShowTemplateBank] = useState(false);

    const fetchSessions = async () => {
        const sessionRef = collection(db, 'clients', id, 'sessions');
        const q = query(sessionRef, orderBy('sessionDateTime', 'desc'));
        const snapshot = await getDocs(q);
        const sessionList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
        setSessions(sessionList);
    };

    useEffect(() => {
        const fetchClient = async () => {
            const docRef = doc(db, 'clients', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setClient({ id: docSnap.id, ...docSnap.data() });
            } else {
                console.log('No such client!');
            }
        };

        const fetchTherapists = async () => {
            const snapshot = await getDocs(collection(db, 'roles'));
            const list = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.role === 'student') {
                    list.push({ uid: doc.id, displayName: data.displayName || 'Unnamed' });
                }
            });
            setTherapists(list);
        };

        fetchClient();
        fetchSessions();
        fetchTherapists();
    }, [id]);

    const handleDelete = async () => {
        await deleteDoc(doc(db, 'clients', id));
        navigate('/dashboard');
    };

    const handleAddSession = async () => {
        const finalName = therapistUID === 'custom' ? customTherapist : therapistName;
        const finalUID = therapistUID === 'custom' ? '' : therapistUID;

        if (newSession.trim() === '' || finalName.trim() === '') return;

        // ⚠️ 正確使用 Timestamp 儲存
        let sessionDateTime;
        if (customDate) {
            sessionDateTime = Timestamp.fromDate(new Date(customDate));
        } else {
            sessionDateTime = serverTimestamp();
        }

        const sessionRef = collection(db, 'clients', id, 'sessions');
        await addDoc(sessionRef, {
            note: newSession,
            therapistName: finalName,
            therapistUID: finalUID,
            sessionDateTime: sessionDateTime,
            createdAt: serverTimestamp(),
        });

        setNewSession('');
        setTherapistName('');
        setTherapistUID('');
        setCustomTherapist('');
        setCustomDate('');
        fetchSessions();
    };

    const handleDeleteSession = async (sessionId) => {
        await deleteDoc(doc(db, 'clients', id, 'sessions', sessionId));
        fetchSessions();
    };

    const startEditSession = (session) => {
        setEditingSessionId(session.id);
        setEditedNote(session.note);
    };

    const saveEditedSession = async () => {
        if (!editingSessionId || editedNote.trim() === '') return;
        const sessionRef = doc(db, 'clients', id, 'sessions', editingSessionId);
        await updateDoc(sessionRef, { note: editedNote });
        setEditingSessionId(null);
        setEditedNote('');
        fetchSessions();
    };

    if (!client) return <div className="p-4">Loading...</div>;

    return (
        <div className="p-6 font-sans">
            <div className="mb-4 space-x-4">
                <Link to="/dashboard" className="text-blue-600 hover:underline">
                    ← Back to Dashboard
                </Link>
                <Link
                    to={`/client/${client.id}/calendar`}
                    className="text-blue-600 hover:underline"
                >
                    📅 View Calendar
                </Link>
            </div>

            <h2 className="text-2xl font-bold mb-4">Client Detail</h2>

            <div className="bg-white shadow p-4 rounded mb-4">
                <p><strong>Name:</strong> {client.name}</p>
                <p><strong>Email:</strong> {client.email || '-'}</p>

                {showMore && (
                    <>
                        <p><strong>Phone:</strong> {client.contact || '-'}</p>
                        <p><strong>District:</strong> {client.location || '-'}</p>
                        <p><strong>Birth Date:</strong> {client.birthDate || '-'}</p>
                        <p><strong>Age:</strong> {client.birthDate ? calculateAge(client.birthDate) : '-'}</p>
                        <p><strong>Gender:</strong> {client.gender || '-'}</p>
                        <p><strong>Therapist:</strong> {client.therapist || '-'}</p>
                        <p><strong>Student Therapist:</strong> {client.studentTherapist || '-'}</p>
                    </>
                )}

                <button
                    onClick={() => setShowMore(!showMore)}
                    className="mt-2 text-blue-500 underline"
                >
                    {showMore ? 'Show Less' : 'Show More'}
                </button>

                <button
                    onClick={handleDelete}
                    className="mt-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 block"
                >
                    Delete Client
                </button>
            </div>

            <div className="bg-white shadow p-4 rounded mb-4">
                <h3 className="text-lg font-semibold mb-2">Add Session Note</h3>

                <select
                    value={therapistUID}
                    onChange={(e) => {
                        const uid = e.target.value;
                        setTherapistUID(uid);
                        const matched = therapists.find(t => t.uid === uid);
                        setTherapistName(matched?.displayName || '');
                    }}
                    className="w-full p-2 border rounded mb-2"
                >
                    <option value="">-- Select therapist --</option>
                    {therapists.map((t) => (
                        <option key={t.uid} value={t.uid}>{t.displayName}</option>
                    ))}
                    <option value="custom">Other (type manually)</option>
                </select>

                {therapistUID === 'custom' && (
                    <input
                        type="text"
                        value={customTherapist}
                        onChange={(e) => setCustomTherapist(e.target.value)}
                        className="w-full p-2 border rounded mb-2"
                        placeholder="Enter therapist name"
                    />
                )}

                <input
                    type="datetime-local"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="w-full p-2 border rounded mb-2"
                />

                <textarea
                    value={newSession}
                    onChange={(e) => setNewSession(e.target.value)}
                    className="w-full p-2 border rounded mb-2"
                    rows={3}
                    placeholder="Write session notes here..."
                ></textarea>

                <button
                    onClick={() => setShowTemplateBank(true)}
                    className="bg-brown-600 text-white px-4 py-2 rounded hover:bg-brown-700"
                >
                    🧠 Open Template Bank / AI Suggestion
                </button>

                <button
                    onClick={handleAddSession}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    Add Session
                </button>
            </div>

            {showTemplateBank && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full p-4 relative">
                        <button
                            onClick={() => setShowTemplateBank(false)}
                            className="absolute top-2 right-2 text-gray-600 hover:text-black text-xl"
                        >
                            ✖
                        </button>
                        <TemplateBank
                            onSelect={(text) => {
                                if (editingSessionId) {
                                    setEditedNote(prev => prev + '\n' + text);
                                } else {
                                    setNewSession(prev => prev + '\n' + text);
                                }
                                setShowTemplateBank(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <div className="bg-white shadow p-4 rounded">
                <h3 className="text-lg font-semibold mb-2">Session History</h3>
                {sessions.length === 0 ? (
                    <p>No sessions found.</p>
                ) : (
                    <div className="space-y-2">
                        {sessions.map((session) => (
                            <div key={session.id} className="border rounded overflow-hidden">
                                <button
                                    onClick={() =>
                                        setExpandedSession((prev) => (prev === session.id ? null : session.id))
                                    }
                                    className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 font-medium"
                                >
                                    📅 {(() => {
                                        try {
                                            return session.sessionDateTime?.toDate().toLocaleString() || 'No date';
                                        } catch (e) {
                                            return 'No date';
                                        }
                                    })()} — 🧑‍⚕️ {session.therapistName || 'Unknown therapist'}
                                </button>

                                {expandedSession === session.id && (
                                    <div className="px-4 py-2 bg-white text-sm whitespace-pre-line border-t">
                                        {editingSessionId === session.id ? (
                                            <>
                                                <textarea
                                                    value={editedNote}
                                                    onChange={(e) => setEditedNote(e.target.value)}
                                                    className="w-full p-2 border rounded mb-2"
                                                    rows={3}
                                                ></textarea>

                                                <button
                                                    onClick={() => setShowTemplateBank(true)}
                                                    className="mt-2 text-sm text-blue-600 hover:underline mr-2"
                                                >
                                                    🧠 使用 Template Bank
                                                </button>

                                                <button
                                                    onClick={saveEditedSession}
                                                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 mr-2"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setEditingSessionId(null)}
                                                    className="bg-gray-400 text-white px-3 py-1 rounded hover:bg-gray-500"
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <p>{session.note}</p>
                                                <div className="mt-2 space-x-2">
                                                    <button
                                                        onClick={() => startEditSession(session)}
                                                        className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSession(session.id)}
                                                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClientDetail;
